import React, { useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { HiOutlineEyeOff, HiOutlineEye } from 'react-icons/hi';

import { useInputId } from './hooks';

const FormPassword = React.forwardRef(
  (
    {
      id: defaultId,
      label,
      name,
      onChange,
      onSetFieldValue,
      value,
      error,
      required,
      noAutoFill,
      ...props
    },
    ref
  ) => {
    const [inputType, setInputType] = useState('password');
    const [id] = useInputId(defaultId);
    const handleChange = ({ target }) => {
      if (typeof onChange === 'function') {
        onChange((state) => ({
          ...state,
          [name]: target.value,
        }));
      }
      if (typeof onSetFieldValue === 'function') {
        onSetFieldValue(name, target.value);
      }
    };
    const handleTogglePassword = () => {
      if (inputType === 'password') setInputType('text');
      else setInputType('password');
    };
    const isError = !!error;

    return (
      <div className="flex flex-col">
        <label htmlFor={id} className="block font-medium mb-2 text-gray-500">
          <span>{label}</span>
          {required && <span className="ml-1 font-light text-red-500">*</span>}
        </label>
        <div
          className={cn(
            'relative border rounded-2xl bg-white p-3 shadow-sm focus-within:ring-1',
            isError
              ? 'border-red-300 focus-within:ring-red-500 focus-within:border-red-500 animate-wiggle'
              : 'border-gray-300 focus-within:ring-primary-600 focus-within:border-primary-600'
          )}
        >
          <input
            id={id}
            ref={ref}
            name="name"
            className={cn(
              'block w-full border-0 p-0 text-gray-900 placeholder-gray-300 focus:ring-0 text-base',
              'focus:outline-none'
            )}
            onChange={handleChange}
            value={value || ''}
            {...props}
            type={inputType}
            autoComplete={noAutoFill ? 'new-password' : 'on'}
          />
          <button
            type="button"
            className="absolute inset-y-0 right-0 pr-3 flex items-center focus:outline-none"
            onClick={handleTogglePassword}
            tabIndex={-1}
          >
            {inputType === 'password' ? (
              <HiOutlineEyeOff className="text-gray-400 w-5 h-5" />
            ) : (
              <HiOutlineEye className="text-gray-400 w-5 h-5" />
            )}
          </button>
        </div>
      </div>
    );
  }
);

FormPassword.defaultProps = {
  id: '',
  type: 'text',
  error: null,
  required: false,
  onChange: false,
  onSetFieldValue: false,
  noAutoFill: false,
};

FormPassword.propTypes = {
  id: PropTypes.string,
  type: PropTypes.string,
  required: PropTypes.bool,
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  error: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onSetFieldValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  noAutoFill: PropTypes.bool,
};

export default React.memo(FormPassword);
